import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef as TanStackColumnDef,
  type RowSelectionState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { normalizeListResponse } from "./normalizeListResponse";
import { DataGridBulkActions } from "./DataGridBulkActions";
import { DataGridDetailsDrawer } from "./DataGridDetailsDrawer";
import { DataGridError } from "./DataGridError";
import { DataGridFilters } from "./DataGridFilters";
import { DataGridPagination } from "./DataGridPagination";
import { DataGridTable } from "./DataGridTable";
import { DataGridToolbar } from "./DataGridToolbar";
import { useDataGrid } from "./hooks/useDataGrid";
import { useGridPreferences } from "./hooks/useGridPreferences";
import { type TDataGridProps, type TPaginationConfig } from "./types";
import { clientSearch, clientSort } from "./utils";

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Top-level, config-driven data grid. Wires the config to the fetch/state
 * machine (`useDataGrid`), localStorage preferences (`useGridPreferences`), and
 * a TanStack table instance that owns column visibility/order + row selection.
 */
export function DataGrid<TRow>(props: TDataGridProps<TRow>) {
  const {
    getRowId,
    persistKey,
    data,
    columns,
    toolbar = {},
    filters = [],
    selection = false,
    bulkActions,
    rowActions,
    details,
    onRowClick,
    pagination,
    emptyState,
    className,
    ariaLabel,
  } = props;

  const paginationConfig: Required<TPaginationConfig> = {
    mode: pagination?.mode ?? "offset",
    pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
    pageSizeOptions: pagination?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS,
  };

  // Preferences (localStorage-backed).
  const prefs = useGridPreferences(persistKey, columns, paginationConfig.pageSize);

  // Core state machine + fetch.
  const grid = useDataGrid<TRow>({
    data,
    columns,
    filters,
    pageSize: prefs.pageSize,
  });

  // TanStack table: owns column visibility/order + row selection state.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const visibilityState: VisibilityState = prefs.columnVisibility;

  // Minimal TanStack column defs (id + accessor placeholder); actual rendering
  // is done by our own DataGridTable / cell renderers.
  const tanstackColumns = useMemo<TanStackColumnDef<TRow>[]>(
    () => columns.map((column) => ({ id: column.id, accessorKey: column.id, enableHiding: column.hideable !== false })),
    [columns]
  );

  const table = useReactTable<TRow>({
    data: grid.rows,
    columns: tanstackColumns,
    getRowId,
    state: { columnVisibility: visibilityState, rowSelection, columnOrder: prefs.columnOrder },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === "function" ? updater(visibilityState) : updater;
      prefs.setColumnVisibility(next as Record<string, boolean>);
    },
    onColumnOrderChange: (updater) => {
      const next = typeof updater === "function" ? updater(prefs.columnOrder) : updater;
      prefs.setColumnOrder(next);
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: selection,
    manualPagination: true,
    manualSorting: data.mode === "server",
    manualFiltering: data.mode === "server",
    getCoreRowModel: getCoreRowModel(),
  });

  // Visible columns in the preferred order.
  const orderedVisibleColumns = useMemo(() => {
    const order = prefs.columnOrder.length ? prefs.columnOrder : columns.map((c) => c.id);
    const byId = new Map(columns.map((c) => [c.id, c] as const));
    return order
      .map((id) => byId.get(id))
      .filter((c): c is (typeof columns)[number] => Boolean(c))
      .filter((c) => visibilityState[c.id] !== false);
  }, [columns, prefs.columnOrder, visibilityState]);

  // Selection helpers keyed by our stable getRowId.
  const selectedIds = useMemo(() => new Set(Object.keys(rowSelection).filter((id) => rowSelection[id])), [rowSelection]);

  const pageIds = useMemo(() => grid.rows.map(getRowId), [grid.rows, getRowId]);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = pageIds.some((id) => selectedIds.has(id)) && !allSelected;

  const toggleRow = useCallback(
    (id: string) => setRowSelection((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );
  const toggleAll = useCallback(() => {
    setRowSelection((prev) => {
      const next = { ...prev };
      if (pageIds.every((id) => next[id])) {
        for (const id of pageIds) delete next[id];
      } else {
        for (const id of pageIds) next[id] = true;
      }
      return next;
    });
  }, [pageIds]);
  const clearSelection = useCallback(() => setRowSelection({}), []);

  const selectedRows = useMemo(
    () => grid.rows.filter((row) => selectedIds.has(getRowId(row))),
    [grid.rows, selectedIds, getRowId]
  );

  // Details drawer state.
  const [detailRow, setDetailRow] = useState<TRow | null>(null);
  const handleRowClick = useCallback(
    (row: TRow) => {
      if (details) {
        setDetailRow(row);
      } else if (onRowClick) {
        onRowClick(row);
      }
    },
    [details, onRowClick]
  );
  const rowClickHandler = details || onRowClick ? handleRowClick : undefined;

  // Export: current page rows are already available; "all filtered" refetches
  // a large page (server) or filters the full list (client).
  const getAllRows = useCallback(async (): Promise<TRow[]> => {
    if (data.mode === "client") {
      const searchFields = data.mode === "client" ? data.searchFields : undefined;
      let rows = clientSearch(grid.rows, "", searchFields);
      // grid.rows is the current page only; for client mode refetch full list.
      const raw = await data.fetcher({
        search: grid.searchInput.trim(),
        filters: {},
        limit: Number.MAX_SAFE_INTEGER,
        offset: 0,
        page: 1,
        per_page: Number.MAX_SAFE_INTEGER,
        signal: new AbortController().signal,
      });
      const normalized = normalizeListResponse<TRow>(raw);
      rows = clientSearch(normalized.rows, grid.searchInput.trim(), searchFields);
      const sortColumn = grid.sort ? columns.find((c) => c.id === grid.sort?.columnId) : undefined;
      if (grid.sort) rows = clientSort(rows, sortColumn, grid.sort.order);
      return rows;
    }
    // Server mode: request a large page with the same search/sort/filters.
    const raw = await data.fetcher({
      search: grid.searchInput.trim(),
      sort_by: grid.sort ? (columns.find((c) => c.id === grid.sort?.columnId)?.sortKey ?? grid.sort?.columnId) : undefined,
      sort_order: grid.sort?.order,
      filters: grid.filterValues,
      limit: Math.max(grid.total, prefs.pageSize),
      offset: 0,
      page: 1,
      per_page: Math.max(grid.total, prefs.pageSize),
      signal: new AbortController().signal,
    });
    return normalizeListResponse<TRow>(raw).rows;
  }, [data, grid, columns, prefs.pageSize]);

  const showToolbar = Object.keys(toolbar).length > 0;
  const showFilters = filters.length > 0;
  const hasActiveQuery = grid.searchInput.trim() !== "" || grid.activeFilterCount > 0;

  return (
    <div className={className}>
      <div className="space-y-4">
        {showToolbar && (
          <DataGridToolbar
            config={toolbar}
            columns={columns}
            searchValue={grid.searchInput}
            onSearchChange={grid.setSearchInput}
            loading={grid.loading}
            onRefresh={grid.refetch}
            columnVisibility={visibilityState}
            onColumnVisibilityChange={(v) => table.setColumnVisibility(v)}
            density={prefs.density}
            onDensityChange={prefs.setDensity}
            currentRows={grid.rows}
            getAllRows={getAllRows}
            exportFilenameBase={persistKey}
          />
        )}

        {showFilters && (
          <DataGridFilters
            filters={filters}
            values={grid.filterValues}
            onChange={grid.setFilterValue}
            onClear={grid.clearFilters}
            activeCount={grid.activeFilterCount}
          />
        )}

        {selection && bulkActions && selectedRows.length > 0 && (
          <DataGridBulkActions
            selectedCount={selectedRows.length}
            actions={bulkActions(selectedRows)}
            onClearSelection={clearSelection}
          />
        )}

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {grid.error ? (
            <DataGridError
              message={grid.error}
              onRetry={grid.refetch}
            />
          ) : (
            <>
              <DataGridTable
                columns={orderedVisibleColumns}
                rows={grid.rows}
                getRowId={getRowId}
                loading={grid.loading}
                density={prefs.density}
                ariaLabel={ariaLabel}
                sort={grid.sort}
                onToggleSort={grid.toggleSort}
                selectionEnabled={selection}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleAll={toggleAll}
                allSelected={allSelected}
                someSelected={someSelected}
                rowActions={rowActions}
                onRowClick={rowClickHandler}
                emptyState={emptyState}
                filtered={hasActiveQuery}
              />
              {grid.total > 0 && (
                <DataGridPagination
                  page={grid.page}
                  pageCount={grid.pageCount}
                  pageSize={prefs.pageSize}
                  total={grid.total}
                  offset={grid.offset}
                  rowCount={grid.rows.length}
                  pageSizeOptions={paginationConfig.pageSizeOptions}
                  onPrev={grid.prevPage}
                  onNext={grid.nextPage}
                  onPageSizeChange={prefs.setPageSize}
                />
              )}
            </>
          )}
        </div>
      </div>

      {details && (
        <DataGridDetailsDrawer
          open={detailRow !== null}
          onOpenChange={(open) => {
            if (!open) setDetailRow(null);
          }}
          title={detailRow && details.title ? details.title(detailRow) : undefined}
        >
          {detailRow && details.render(detailRow)}
        </DataGridDetailsDrawer>
      )}
    </div>
  );
}
